import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Fingerprint } from 'lucide-react'
import { AuthError, claimAccountOpen, signIn } from '../api/auth'
import { passkeysAvailable, signInWithPasskey, wasCancelled } from '../api/passkeys'
import { landingAfterSignIn } from '../signInLanding'
import { SESSION_KEY, useSetSession } from '../useSession'
import AuthLayout, { Field, FormError, SubmitButton } from './AuthLayout'

export default function SignInView() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<AuthError | null>(null)
  const [busy, setBusy] = useState(false)
  const [passkeyBusy, setPasskeyBusy] = useState(false)
  const [passkeyError, setPasskeyError] = useState<string | null>(null)

  const queryClient = useQueryClient()

  /*
   * Whether families may set up their own login yet (D-614).
   *
   * Asked rather than assumed, because the same switch closes the door on the
   * server - a link offering a form that is refused is worse than no link.
   */
  const { data: claimOpen } = useQuery({
    queryKey: ['claim-account-available'],
    queryFn: claimAccountOpen,
    staleTime: Infinity,
  })

  // Asked once, and the button does not exist until the answer is yes.
  const { data: canUsePasskeys } = useQuery({
    queryKey: ['passkeys-available'],
    queryFn: passkeysAvailable,
    staleTime: Infinity,
  })

  const navigate = useNavigate()
  const setSession = useSetSession()
  const [params] = useSearchParams()

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)

    try {
      const session = await signIn(email, password)
      setSession(session)

      /*
       * Back to whatever they were trying to reach, or to the root — which
       * decides where this person belongs (D-176).
       *
       * **It used to default to `/calendar`**, so signing in sent a parent to
       * the studio's whole timetable no matter what her capabilities were. The
       * root already knew better; sign-in simply never asked it.
       *
       * **And it used to honour `next` blindly** (D-595). The address is
       * written by whichever signed-out visit came before, so a new parent
       * signing in on a browser that had been bounced off an admin screen
       * landed on *Not your page*. The rail decides whether she may go there.
       */
      navigate(landingAfterSignIn(session, params.get('next')), { replace: true })
    } catch (e) {
      setError(
        e instanceof AuthError
          ? e
          : new AuthError('Could not reach the studio system. Check your connection.', 0),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Use the email address the studio has on file."
      footer={
        <>
          Trouble signing in? Ask Tonya to send a new invitation.
        </>
      }
    >
      {error && <FormError title={error.message} detail={error.detail} />}

      <form onSubmit={submit} noValidate>
        <Field
          label="Email"
          type="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          // Lets a password manager offer the right entry, and on a phone
          // brings up the keyboard with the @ key.
          autoComplete="username"
          autoFocus
          required
        />

        <Field
          label="Password"
          type="password"
          name="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />

        <SubmitButton busy={busy}>Sign in</SubmitButton>
      </form>

      {/*
        Face ID, a fingerprint, or a security key (D-320).
 
        <b>Below the password, not instead of it.</b> A passkey lives on one
        device, so the password is the way back from a lost phone and this is
        opt-in on top. Most families and teachers are on handsets, where typing
        a password is the worst part of using the app.

        Hidden where the browser cannot do it at all, rather than shown and
        failing - an old tablet in the office should see the form it can use.
      */}
      {canUsePasskeys && (
        <>
          <div className="my-4 flex items-center gap-3">
            <span className="h-px flex-1 bg-line" />
            <span className="text-[11.5px] font-semibold tracking-wide text-ink-3 uppercase">or</span>
            <span className="h-px flex-1 bg-line" />
          </div>

          <button
            type="button"
            disabled={passkeyBusy}
            onClick={async () => {
              setPasskeyBusy(true)
              setPasskeyError(null)

              try {
                await signInWithPasskey()
                await queryClient.invalidateQueries({ queryKey: SESSION_KEY })
                navigate('/', { replace: true })
              } catch (e) {
                // Tapping Cancel is not a fault and must not be dressed as one.
                if (!wasCancelled(e)) setPasskeyError((e as Error).message)
              } finally {
                setPasskeyBusy(false)
              }
            }}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-line-2 bg-surface px-4 py-2.5 text-[14px] font-semibold text-ink-2 transition-colors hover:border-accent hover:text-accent-2 disabled:opacity-60"
          >
            <Fingerprint size={16} strokeWidth={2} />
            {passkeyBusy ? 'Waiting for your device...' : 'Sign in with a passkey'}
          </button>

          {passkeyError && (
            <p role="alert" className="mt-2 text-center text-[12.5px] text-crit">
              {passkeyError}
            </p>
          )}
        </>
      )}

      {/*
        The way in for 163 families who have never signed in (D-155). Without a
        route from this page, the claim form is a URL somebody has to be told —
        and the person who would have to tell them is the one with no time.
      */}
      {/* The reset path the reset email itself points people at. */}
      <p className="mt-4 text-center text-[13px] text-ink-3">
        <Link to="/forgot-password" className="font-semibold text-accent">
          Forgotten your password?
        </Link>
      </p>

      {claimOpen === true && (
        <p className="mt-5 text-center text-[13px] text-ink-3">
          First time here?{' '}
          <Link to="/claim-account" className="font-semibold text-accent">
            Set up your account
          </Link>
        </p>
      )}
    </AuthLayout>
  )
}
